import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useRouter } from "next/router";
import { useForm, FormProvider } from "react-hook-form";
import { MainTemplate } from "@/components/templates";
import { Font, useSnackBar } from "@moneyforward/mfx_biz_react_components";
import {
  RegisterNewCompanyMutation,
  useRegisterNewCompanyMutation,
  VerifyProvisionalRegistrationTokenMutation,
  useVerifyProvisionalRegistrationTokenMutation,
} from "@/generated/graphql";
import { RegisterCompanyForm } from "@/components/organisms";
import { AuthContext } from "@/contexts/AuthContext";
import { PageTitle } from "@/components/molecules";
import { css } from "@emotion/react";
import { ErrorCode, errorCodeIsIncluded, moveToErrorPage } from "@/libs/errors";
import { useRegistrationRedirectParams } from "@/components/hooks/useRegistrationRedirectParams";
import useRollbarUtil from "../hooks/useRollbarUtil";

type FormValues = {
  username: string;
  password: string;
  lastName: string;
  lastNameKana: string;
  middleName: string;
  middleNameKana: string;
  firstName: string;
  firstNameKana: string;
};

export const RegisterCompanyPage = () => {
  const { rollbar, getNotifyErrorHandlerForGraphQL } = useRollbarUtil();
  const router = useRouter();
  const [verifying, setVerifying] = useState(true);
  const [email, setEmail] = useState("");
  const { app } = router.query;

  const token = useMemo(() => {
    return router.query.token as string;
  }, [router.query.token]);

  const methods = useForm<FormValues>({
    reValidateMode: "onBlur",
    defaultValues: {
      username: "",
      password: "",
      lastName: "",
      lastNameKana: "",
      middleName: "",
      middleNameKana: "",
      firstName: "",
      firstNameKana: "",
    },
  });

  const { notify } = useSnackBar();

  const [verifyProvisionalRegistrationToken] =
    useVerifyProvisionalRegistrationTokenMutation({
      onCompleted: (data: VerifyProvisionalRegistrationTokenMutation) => {
        if (!data.verifyProvisionalRegistrationToken.result) {
          moveToErrorPage(ErrorCode.INVALID_EMAIL_AUTH_TOKEN);
          return;
        }
        setVerifying(false);
        setEmail(data.verifyProvisionalRegistrationToken.email);
      },
      onError: (err) => {
        if (errorCodeIsIncluded(err, ErrorCode.INVALID_EMAIL_AUTH_TOKEN)) {
          moveToErrorPage(ErrorCode.INVALID_EMAIL_AUTH_TOKEN);
          return;
        }
        rollbar.error("Failed to verify provisional registration token", err);
        moveToErrorPage();
      },
    });

  const authContext = useContext(AuthContext);
  const [registerNewCompany, { loading: registerNewCompanyLoading }] =
    useRegisterNewCompanyMutation({
      onCompleted: async (data: RegisterNewCompanyMutation) => {
        if (!data.registerNewCompany.result) {
          notify("登録に失敗しました");
          return;
        }

        try {
          await authContext.signIn(
            methods.getValues().username,
            methods.getValues().password
          );
        } catch (err) {
          rollbar.error("Failed to sign in", err);
        }
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useRegistrationRedirectParams(router, app);
      },
      onError: getNotifyErrorHandlerForGraphQL(
        "Failed to register new company",
        "ログインに失敗しました"
      ),
    });

  const handleSubmit = useCallback(
    async ({
      username,
      password,
      lastName,
      lastNameKana,
      middleName,
      middleNameKana,
      firstName,
      firstNameKana,
    }: FormValues) => {
      if (registerNewCompanyLoading) return;
      await registerNewCompany({
        variables: {
          input: {
            token,
            username,
            companyName: "",
            password,
            lastName,
            lastNameKana,
            middleName,
            middleNameKana,
            firstName,
            firstNameKana,
          },
        },
      });
    },
    [registerNewCompany, token]
  );

  useEffect(() => {
    if (!token) return;
    verifyProvisionalRegistrationToken({
      variables: { input: { token } },
    });
  }, [token]);

  return (
    <MainTemplate title={`利用者の新規登録`}>
      {verifying ? (
        <div css={messageWrapper}>
          <Font variant="m" component="p">
            確認中...
          </Font>
        </div>
      ) : (
        <>
          <PageTitle
            mainTitle={`利用者の新規登録`}
            subTitle={
              <>
                Mikatano シリーズをご利用いただき、ありがとうございます。
                <br />
                まずは利用者情報とログイン情報の登録をしてください。
              </>
            }
          />
          <FormProvider {...methods}>
            <RegisterCompanyForm email={email} onSubmit={handleSubmit} />
          </FormProvider>
        </>
      )}
    </MainTemplate>
  );
};

const messageWrapper = css({
  display: "flex",
  justifyContent: "center",
});
